import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { useSession } from "../auth/SessionProvider";
import { getSupabaseBrowserClient } from "../integrations/supabase/client";
import { BoxState } from "../ui/BoxState";
import { Callout } from "../ui/Callout";
import { AuthFrame } from "./auth/AuthFrame";
import { CONTACT_EMAIL, CONTACT_MAILTO } from "../lib/contact";

/**
 * Where a link from an email lands.
 *
 * `PORTAL_NO_INVITATION_FLOW` recorded what this route used to be: a placeholder, so a link mailed
 * to a new client arrived at a page that did nothing with it. That is why a password was set by hand
 * in the Supabase dashboard for every person given access.
 *
 * Three shapes arrive here and all three are handled, because which one arrives is decided by the
 * email template and by which device the person is holding.
 *
 * **`token_hash` and `type`.** The one that works from any device. `verifyOtp` exchanges the hash
 * for a session with no local state involved, which is what the templates in `supabase/templates/`
 * produce. It is the only shape that survives the ordinary case of a link opened on a phone when the
 * request was made on a laptop.
 *
 * **It is exchanged when a person presses the button, and never when the page loads.** It used to
 * be exchanged from an effect, and the property that made it work from any device is the property
 * that made that unsafe: the hash needs nothing but itself, so anything that loads the page with its
 * JavaScript running spends it. Mail filters do exactly that. Microsoft 365's link scanning opens a
 * link in a real browser before the recipient ever sees the message, and a single-use invitation
 * opened that way is dead by the time the client clicks it. In the walkthrough a customer's
 * invitation was spent the same way by a browser that crashed after loading it; he got in through
 * Glömt lösenordet, whose link had the same weakness. A scanner loads pages and does not press
 * buttons, so the press is the whole fix and nothing else here changed.
 *
 * **`code`.** The PKCE shape, which a default Supabase template produces.
 * `getSupabaseBrowserClient` is configured with `detectSessionInUrl`, so the client exchanges it
 * while it initialises and there is nothing for this route to do except read the result. It only
 * works in the browser that started the flow, because the code verifier is in that browser's
 * storage. `getSession` waits for that initialisation, so a status of `signed-out` here means the
 * exchange was attempted and did not produce a session rather than that it has not run yet. A
 * scanner cannot spend it for the same reason it only works in one browser: the verifier is not in
 * the scanner's.
 *
 * **`error` and `error_description`.** GoTrue's own refusal, and an expired or already-used link is
 * by far the most common thing that reaches this screen.
 *
 * The route decides where to go and grants nothing. A session that arrives here is a session GoTrue
 * issued, and every gateway re-derives access from it exactly as it does for a session from the
 * login form.
 */

type Exchange =
  | { kind: "waiting" }
  | { kind: "working" }
  | { kind: "failed" }
  | { kind: "done"; to: string };

/** The link types this portal ever mails, mapped to the screen each one is for. */
const DESTINATION: Record<string, string> = {
  invite: "/invite",
  recovery: "/new-password",
  signup: "/overview",
  // Namn och inloggning, where the sign-in address is shown, since the settings split of 2026-09-24.
  email_change: "/settings/account",
  magiclink: "/overview"
};

/**
 * What the page says before the press, per link type.
 *
 * The two the portal actually mails get their own words, because they are the first screen a new
 * client sees and the screen a locked-out one sees. Every other type GoTrue can produce gets a
 * neutral one rather than a guess at what it is for.
 *
 * The invitation's lead lost its second sentence on 2026-09-24 with the client sketch
 * (`prototype/kund/pages-auth.js`): "Nästa steg är att välja lösenordet du loggar in med" is what
 * the button under it says in four words.
 */
const WAITING: Record<string, { heading: string; lead?: string; action: string }> = {
  invite: {
    heading: "Välkommen till kundportalen",
    lead: "Petrovic Systems har skapat ett konto åt dig.",
    action: "Fortsätt och välj lösenord"
  },
  recovery: {
    heading: "Välj ett nytt lösenord",
    action: "Fortsätt"
  }
};

const WAITING_FALLBACK = { heading: "Bekräfta länken", lead: undefined, action: "Fortsätt" };

/**
 * Why there is a button at all, said once and without the word "scanner".
 *
 * A person who has clicked a link and then meets a second click deserves to know it is deliberate.
 * Without the sentence the button reads as a step somebody forgot to remove.
 */
const WHY_THE_BUTTON =
  "Knappen finns så att ett e-postprogram som öppnar länkar av sig självt inte kan använda upp länken.";

/**
 * One sentence for every way a link can be dead, in Swedish, and never GoTrue's own words.
 *
 * `error_description` arrives as English prose written for a developer, and the portal's rule for
 * server strings is the one `app/lib/errors.ts` states: a screen that echoes an upstream message
 * back to a client is leaking its internals in a language the client did not choose. The distinction
 * the description would add is between expired and already used, and neither changes what the person
 * does next.
 *
 * Its first sentence, "Länken gick inte att använda", went on 2026-09-24 with the client sketch,
 * because the heading above it now says the same thing.
 */
const FAILED_TEXT = "Den kan ha gått ut eller redan ha använts.";

export function AuthCallbackRoute() {
  const [params] = useSearchParams();
  const { status } = useSession();
  const [exchange, setExchange] = useState<Exchange>({ kind: "waiting" });

  /**
   * The double-press guard, in a ref rather than in state.
   *
   * State is not enough on its own. Two clicks dispatched before React re-renders both read the
   * `waiting` the first click has not replaced yet, and the second would present a hash the first had
   * just spent, turning a successful exchange into the failed screen. A ref is written synchronously
   * inside the first handler, so the second sees it. It is never reset: after a press the page either
   * leaves or says the link is dead, and neither has anything left to press for.
   */
  const pressed = useRef(false);
  const mounted = useRef(true);

  useEffect(() => {
    // Set in the body and not only in the initial value, because StrictMode runs the cleanup once
    // and then the effect again on the same instance.
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const tokenHash = params.get("token_hash");
  const type = params.get("type");
  const code = params.get("code");
  const refused = Boolean(params.get("error") ?? params.get("error_code"));

  /**
   * A hash with no type cannot be exchanged, because `verifyOtp` needs both.
   *
   * The templates always write both, so this is a link a mail client truncated or a person copied
   * by hand. The effect used to return early for it and the render fell through to the spinner,
   * which then turned forever: the screen pretending to work. It is a link that does not work, and
   * the failed screen is the one that says so and offers a new one.
   */
  const malformed = Boolean(tokenHash) && !type;

  /**
   * A refusal in the query is derived here and never written to state in an effect.
   *
   * It was an effect once and the browser tests caught what that costs: the first render still held
   * `working`, fell through to the signed-in redirect below, and sent a person holding a dead link
   * cheerfully to the overview one frame before the effect could say otherwise. Nothing about
   * `?error=` needs to be awaited, so nothing about it belongs in an effect.
   */
  const outcome: Exchange = refused || malformed ? { kind: "failed" } : exchange;

  async function spendTheLink() {
    if (pressed.current || refused || !tokenHash || !type) {
      return;
    }

    pressed.current = true;
    setExchange({ kind: "working" });

    const failed = await Promise.resolve()
      .then(() =>
        getSupabaseBrowserClient().auth.verifyOtp({
          token_hash: tokenHash,
          // GoTrue's own vocabulary, passed through rather than re-encoded here. A type this portal
          // does not mail is still a type GoTrue understands, and inventing a mapping for it would
          // be this file deciding something the server already decided.
          type: type as "invite" | "recovery" | "signup" | "email_change" | "magiclink"
        })
      )
      .then(
        ({ error }) => Boolean(error),
        // A request that never reached GoTrue did not spend the link, but the person still cannot
        // go on from here, and a spinner left turning is worse than the screen that offers a new link.
        () => true
      );

    if (mounted.current) {
      setExchange(failed ? { kind: "failed" } : { kind: "done", to: DESTINATION[type] ?? "/overview" });
    }
  }

  if (outcome.kind === "done") {
    return <Navigate to={outcome.to} replace />;
  }

  if (outcome.kind === "failed" || (code && status === "signed-out")) {
    // The contact line stays under the two ways on, although the sketch of 2026-09-24 drew none: a
    // person whose link failed cannot reach the chat, and docs/polish.md P-14 (Mattej, 2026-09-24)
    // is that they are given the address instead.
    return (
      <AuthFrame titleId="callback-heading">
        <h2 id="callback-heading" className="k-auth-title">
          Länken fungerade inte
        </h2>

        <p data-testid="callback-failed">{FAILED_TEXT}</p>

        <Link className="button is-primary is-block" to="/recover">
          <span>Begär en ny länk</span>
        </Link>

        <div className="k-auth-links">
          <Link to="/login">Till inloggningen</Link>
          <p className="k-auth-note" data-testid="callback-contact">
            Begär en ny länk från inloggningssidan, eller mejla Petrovic Systems på{" "}
            <a href={CONTACT_MAILTO}>{CONTACT_EMAIL}</a>.
          </p>
        </div>
      </AuthFrame>
    );
  }

  /**
   * A hash waiting for its press, and the same screen while the press is being answered.
   *
   * This comes before every question about the current session, and that order is the decision
   * about a visitor who is already signed in. They still have to press. Redirecting them on the
   * strength of the session they hold would send them to `/invite` or `/new-password` as whoever
   * that session belongs to, and the link may have been mailed to somebody else: a colleague's
   * invitation forwarded to the person who already has an account, or Petrovic opening a client's
   * recovery link in the browser he is signed in to. The new-password screen would then set a
   * password on the wrong account. Pressing exchanges the link, GoTrue answers with a session for
   * the account the link was actually sent to, and the client replaces the one it held. So the press
   * is also what makes the destination screen belong to the right person, and the one extra line
   * below says that in advance rather than letting the account change without warning.
   *
   * It does not wait for `status` to leave `loading` either. The button does nothing with the
   * session, and holding a spinner until the client has initialised would delay the one thing on
   * the page for a question the page does not need answered.
   *
   * While verifying, the button stays where it is, disabled, and the loading state appears beneath
   * it. Swapping the whole screen for a spinner would move the thing the person just pressed out
   * from under the pointer.
   */
  if (tokenHash && type) {
    const copy = WAITING[type] ?? WAITING_FALLBACK;
    const working = outcome.kind === "working";

    return (
      <AuthFrame titleId="callback-heading" testId="callback-waiting">
        <h2 id="callback-heading" className="k-auth-title">
          {copy.heading}
        </h2>

        {copy.lead ? <p>{copy.lead}</p> : null}

        {status === "signed-in" ? (
          <Callout tone="info" testId="callback-signed-in">
            Du är redan inloggad i den här webbläsaren. Fortsätter du loggas du in med kontot som
            länken skickades till.
          </Callout>
        ) : null}

        <button
          type="button"
          className="button is-primary is-block"
          data-testid="callback-continue"
          disabled={working}
          aria-busy={working}
          onClick={() => void spendTheLink()}
        >
          <span>{copy.action}</span>
        </button>

        {working ? (
          <BoxState kind="loading" title="Kontrollerar länken" compact />
        ) : (
          <div className="k-auth-links">
            <p className="k-auth-note">{WHY_THE_BUTTON}</p>
          </div>
        )}
      </AuthFrame>
    );
  }

  // A session still being read, for the `code` shape or a bare address. A wait rather than an answer.
  if (status === "loading") {
    return (
      <AuthFrame>
        <BoxState kind="loading" title="Kontrollerar länken" />
      </AuthFrame>
    );
  }

  if (status === "signed-in") {
    return <Navigate to={DESTINATION[type ?? ""] ?? "/overview"} replace />;
  }

  /**
   * Signed out, nothing in the query and nothing refused. Somebody typed the address, or a link
   * arrived stripped of its query by a mail client that rewrites URLs. Either way there is nothing
   * to verify, and standing on a spinner would be the screen pretending to work.
   */
  return (
    <AuthFrame titleId="callback-heading">
      <h2 id="callback-heading" className="k-auth-title">
        Ingenting att bekräfta
      </h2>
      <p data-testid="callback-empty">
        Den här adressen används av länkar i mejl från portalen och har ingen egen sida.
      </p>
      <Link className="button is-primary is-block" to="/login">
        <span>Till inloggningen</span>
      </Link>
    </AuthFrame>
  );
}
